import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import { Link } from '../../Link'
import type { PictureProps } from '../../Picture'
import { Picture } from '../../Picture'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import { SignupButton } from '../../SignupButton/SignupButton'
import { Modal } from '../Modal'
import { Logo } from './assets/Logo'
import styles from './SignUp.module.scss'

const FULL_ACCESS_DIRECT_LINK =
  'https://account.which.co.uk/checkout-core?purchase=/offers2/Full%2520Access%2520Join%2520Monthly%2520%25C2%25A35%25201%2520month%2520%25C2%25A311.99%2520Monthly&lmo_offer=/offers2/Full%2520Access%2520Join%2520Monthly%2520%25C2%25A35%25201%2520month%2520%25C2%25A311.99%2520Monthly&ltm_gaBundleID=FULLM5001199&ltm_product=core&ltm_firstPaymentAmount=5.00&ltm_recurringPaymentAmount=11.99'
const OTHER_PLANS_LINK = 'https://join.which.co.uk/join/subscribe'
const LOGIN_LINK = 'https://www.which.co.uk/login'

export const SignUp: FunctionComponent<SignUpProps> = ({
  overline,
  title,
  subtitle,
  image,
  closeModal,
  modalLinkDatalayerTrackingId,
  loginClickHandler,
  isPriceTest,
  hidePriceInfo = true,
  ...rest
}) => (
  <Modal
    data-which-id={`${modalLinkDatalayerTrackingId}-popup`}
    closeModal={closeModal}
    {...rest}
  >
    <Logo className={styles.logo} />
    <Picture {...image} />
    <div className={styles.information}>
      <TypographyV2
        tag="h3"
        textStyle="sb-text-heading-overline"
        className={styles.overline}
      >
        {overline}
      </TypographyV2>
      <TypographyV2
        tag="h2"
        textStyle="sb-text-heading-large"
        className={styles.title}
      >
        {title}
      </TypographyV2>
      {subtitle ? (
        <TypographyV2
          tag="p"
          textStyle="sb-text-body-default-regular"
          className={styles.subtitle}
          data-testid="subtitle"
        >
          {subtitle}
        </TypographyV2>
      ) : isPriceTest ? ( // when price test is active - for now added same price as per https://whichonline.atlassian.net/browse/RS-2434
        <TypographyV2
          tag="p"
          textStyle="sb-text-body-default-regular"
          className={styles.subtitle}
          data-testid="subtitle"
        >
          Get instant access to Which? for{' '}
          <TypographyV2
            tag="span"
            textStyle="sb-text-body-default-strong"
            className={styles.priceText}
          >
            £5
          </TypographyV2>{' '}
          for your first month, then{' '}
          <TypographyV2
            tag="span"
            textStyle="sb-text-body-default-strong"
            className={styles.priceText}
          >
            £11.99
          </TypographyV2>{' '}
          a month, cancel at any time.
        </TypographyV2>
      ) : (
        <TypographyV2
          tag="p"
          textStyle="sb-text-body-default-regular"
          className={styles.subtitle}
          data-testid="subtitle"
        >
          Get instant access to Which? for{' '}
          <TypographyV2
            tag="span"
            textStyle="sb-text-body-default-strong"
            className={styles.priceText}
          >
            £5
          </TypographyV2>{' '}
          for your first month, then{' '}
          <TypographyV2
            tag="span"
            textStyle="sb-text-body-default-strong"
            className={styles.priceText}
          >
            £11.99
          </TypographyV2>{' '}
          a month, cancel at any time.
        </TypographyV2>
      )}

      <div className={styles.actionButtonsWrapper}>
        <SignupButton
          captionDatalayerTrackingId="signup-cta-compliance-text"
          buttonDatalayerTrackingId={`${modalLinkDatalayerTrackingId}-signup-cta`}
          isPriceTest={isPriceTest}
          hidePriceInfo={hidePriceInfo}
          buttonLink={{
            href: FULL_ACCESS_DIRECT_LINK,
          }}
        />
        <Link
          className={styles.otherPlansLink}
          data-which-id={`${modalLinkDatalayerTrackingId}-otherplans-link`}
          href={OTHER_PLANS_LINK}
        >
          Explore other plans
        </Link>
      </div>

      <footer className={styles.footer}>
        <TypographyV2 tag="p" textStyle="sb-text-body-default-regular">
          Already a member?{' '}
          <Link
            onClick={loginClickHandler}
            data-which-id={`${modalLinkDatalayerTrackingId}-login-link`}
            href={LOGIN_LINK}
          >
            Login
          </Link>
        </TypographyV2>
      </footer>
    </div>
  </Modal>
)

export type SignUpProps = {
  /**
   * overline.
   */
  overline?: string
  /**
   * Title.
   */
  title: string
  /**
   * Optional subtitle.
   */
  subtitle?: string
  /**
   * Background image.
   */
  image: PictureProps
  /**
   * Callback used to set the parent decision to show the modal to false
   */
  closeModal: () => void
  /**
   * Optional dataLayer tracking attribute to be rendered on the links inside Modal
   */
  modalLinkDatalayerTrackingId?: string
  /**
   * Optional function to be called when login link is clicked
   */
  loginClickHandler?: () => void
  /**
   * Optional property to render sign up price test caption
   */
  isPriceTest?: boolean
  /**
   * Optional property to hide price caption
   */
  hidePriceInfo?: boolean
} & HTMLProps<HTMLDivElement>
